import { Controller } from 'stimulus';

// Per-row controller for the material edit form. Reads canonical unit + the
// list of compatible units from the property select's data attributes, and
// populates a unit picker so the user can enter a value in any compatible
// unit. When the user changes the picker, the visible value field is
// converted from the previous unit to the new one via /units/convert so the
// numbers stay coherent (without this, the displayed number silently changes
// meaning when the picker flips, then the server converts on save and the
// stored value is wrong).
//
// On submit, the picker's value rides along as input_unit; the server
// converts it back to the property's canonical unit (see
// MaterialsController#prepared_material_params).
export default class extends Controller {
  static targets = ['propertySelect', 'unitContainer', 'unitSelect', 'numberValue', 'rangeStart', 'rangeEnd'];

  connect() {
    this.unitsByProperty = JSON.parse(this.data.get('units') || '{}');
    this.update();
  }

  update() {
    if (!this.hasUnitSelectTarget) return;

    const propertyId = this.propertySelectTarget.value;
    const info = this.unitsByProperty[propertyId];

    if (!info || !info.canonical) {
      this.hideContainer();
      this.unitSelectTarget.innerHTML = '';
      this.previousUnit = null;
      return;
    }

    this.unitSelectTarget.innerHTML = '';
    (info.compatible || []).forEach(([label, symbol]) => {
      const opt = document.createElement('option');
      opt.value = symbol;
      opt.textContent = label;
      if (symbol === info.canonical) opt.selected = true;
      this.unitSelectTarget.appendChild(opt);
    });

    this.previousUnit = info.canonical;
    this.showContainer();
  }

  convertValue() {
    const newUnit = this.unitSelectTarget.value;
    const fromUnit = this.previousUnit;

    if (!fromUnit || fromUnit === newUnit) {
      this.previousUnit = newUnit;
      return;
    }

    const inputs = this.activeValueInputs();
    if (inputs.length === 0) {
      this.previousUnit = newUnit;
      return;
    }

    const conversions = inputs
      .filter((input) => input.value !== '' && input.value != null)
      .map((input) => this.convertOne(input, fromUnit, newUnit));

    Promise.all(conversions)
      .then(() => { this.previousUnit = newUnit; })
      .catch(() => {
        this.unitSelectTarget.value = fromUnit;
        if (window.toastr) window.toastr.error('Unable to convert value between units.');
      });
  }

  convertOne(input, from, to) {
    const params = new URLSearchParams({ value: input.value, from, to });
    return fetch(`/units/convert?${params.toString()}`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('convert failed'))))
      .then((data) => { input.value = data.value; });
  }

  activeValueInputs() {
    const inputs = [];
    if (this.hasNumberValueTarget && this.isVisible(this.numberValueTarget)) inputs.push(this.numberValueTarget);
    if (this.hasRangeStartTarget && this.isVisible(this.rangeStartTarget)) inputs.push(this.rangeStartTarget);
    if (this.hasRangeEndTarget && this.isVisible(this.rangeEndTarget)) inputs.push(this.rangeEndTarget);
    return inputs;
  }

  isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  showContainer() {
    if (this.hasUnitContainerTarget) this.unitContainerTarget.classList.remove('hidden');
  }

  hideContainer() {
    if (this.hasUnitContainerTarget) this.unitContainerTarget.classList.add('hidden');
  }
}
